import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { FormattedMessage } from 'react-intl'
import { playerMessages } from './LessonPlayer/CapeeshPlayer.i18n.Messages'

import { useIsPhone } from './LessonPlayer/hooks/useIsPhone'
import useLessonStateMachine, { ScenarioNodeType } from './LessonPlayer/hooks/useLessonStateMachine'
import useLessonScoring from './LessonPlayer/hooks/useLessonScoring'
import LessonHUD from './LessonPlayer/LessonHUD'
import NodeRenderer from './LessonPlayer/NodeRenderer'
import { buildScenarioReport } from './LessonPlayer/LessonReport'
import { studentCourseActions } from '../../../_state/actions'
import { CompletionState } from '../../../Logic/StudentCourseDtos'
import { makeGetRequest } from '../../../_state/helpers/api'
import { configWithAuth, history } from '../../../_state/helpers'
import { LessonType } from '../../../Logic/ScenarioConstants'

const SPEECH_NODE_TYPES = new Set([
    ScenarioNodeType.Speak,
    ScenarioNodeType.TestDefinition,
])

const SCENE_NODE_TYPES = new Set([
    ScenarioNodeType.Watch,
    ScenarioNodeType.Listen,
    ScenarioNodeType.Speak,
    ScenarioNodeType.BranchingMultiChoice,
    ScenarioNodeType.RandomBranch,
    ScenarioNodeType.MultiChoice,
    ScenarioNodeType.ListenMultiChoice,
    ScenarioNodeType.Video,
])

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1A1A2E',
        overflow: 'hidden',
    },
    contentArea: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
    },
    backgroundImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        opacity: 0.3,
        zIndex: 0,
    },
    contentOverlay: {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
    },
    contentOverlayScene: {
        maxWidth: 840,
        padding: '0 20px',
        '@media (max-width: 599px)': {
            padding: '0 4px',
        },
    },
    errorText: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.9)',
        textAlign: 'center',
    },
}))

/**
 * CapeeshPlayer — full-screen lesson player orchestrator.
 *
 * Props:
 *   scenarioV1Dto    – the full CustomLessonScenarioV1Dto
 *   imageMap         – { [originalUrl]: blobUrl } for images
 *   audioMap         – { [originalUrl]: blobUrl } for audio
 *   onClose          – callback to exit the player
 *   dispatch         – Redux dispatch (for reporting)
 *   courseGroupId     – for reporting
 *   l1Id             – for reporting
 *   topicId          – for reporting
 *   customLessonId   – for reporting
 *   generationIndex  – for reporting
 */
const CapeeshPlayer = ({
    scenarioV1Dto,
    imageMap,
    audioMap,
    onClose,
    dispatch,
    courseGroupId,
    l1Id,
    topicId,
    customLessonId,
    generationIndex,
    isNativeSpeaker = false,
}) => {
    const classes = useStyles()
    const isPhone = useIsPhone()
    const nodeStartTime = useRef(Date.now())
    const hasReported = useRef(false)
    const hasTriedAgain = useRef(false)
    const isExamMode = scenarioV1Dto?.lessonType === LessonType.Exam

    // ─── Preload speech token if lesson has speech nodes ──────
    const hasSpeechNodes = useMemo(
        () => scenarioV1Dto?.scenarioNodes?.some((n) => SPEECH_NODE_TYPES.has(n.scenarioNodeType)),
        [scenarioV1Dto]
    )

    useEffect(() => {
        if (hasSpeechNodes) {
            makeGetRequest('speech/recognition/token/v1', configWithAuth()).catch(() => {})
        }
    }, [hasSpeechNodes])

    // ─── State machine ────────────────────────────────────────
    const stateMachine = useLessonStateMachine(scenarioV1Dto)
    const {
        currentNode,
        previousNode,
        currentNodeTypeName,
        currentNodeIndex,
        progressPercent,
        linearNodeOrder,
        goToNode,
        resetToStart,
        getLessonElapsedSeconds,
    } = stateMachine

    // ─── Scoring ──────────────────────────────────────────────
    const scoring = useLessonScoring()

    // ─── Submit lesson report ─────────────────────────────────
    const submitReport = useCallback(
        (completionState = CompletionState.Completed) => {
            if (hasReported.current) return
            if (!dispatch || !courseGroupId || !l1Id || !topicId || !customLessonId) {
                console.warn('[CapeeshPlayer] Missing reporting context — skipping report')
                return
            }
            hasReported.current = true

            const report = buildScenarioReport({
                customLessonId,
                generationIndex: generationIndex || 0,
                triedAgain: hasTriedAgain.current,
                timeToCompleteInSec: getLessonElapsedSeconds(),
                completionState,
                scoring,
            })

            dispatch(
                studentCourseActions.reportLesson(courseGroupId, l1Id, topicId, customLessonId, report)
            )
        },
        [dispatch, courseGroupId, l1Id, topicId, customLessonId, generationIndex, scoring, getLessonElapsedSeconds]
    )

    // ─── Node completion handler ──────────────────────────────
    const handleNodeComplete = useCallback(
        (nodeResult, interactiveResults = null) => {
            if (!currentNode) return

            // Calculate time spent on this node
            const timeUsed = (Date.now() - nodeStartTime.current) / 1000
            const enrichedResult = {
                ...nodeResult,
                nodeId: currentNode.nodeId,
                timeToCompleteInSec: Math.round(timeUsed),
            }

            // Record scoring
            scoring.addStageReport(currentNode, enrichedResult, interactiveResults)

            // Navigate to next node
            const optionIndex = nodeResult.chosenAlternativeIndex || 0
            const nextId = currentNode.options?.[optionIndex]?.nextScenarioNodeId
            if (nextId) {
                nodeStartTime.current = Date.now()
                goToNode(nextId)
            }
        },
        [currentNode, scoring, goToNode]
    )

    // ─── Quit handler (submit report + close) ─────────────────
    const handleQuit = useCallback(() => {
        const state = currentNode?.scenarioNodeType === ScenarioNodeType.Debrief
            ? CompletionState.Completed
            : CompletionState.Exited
        submitReport(state)
        onClose()
        history.push('/student')
    }, [submitReport, onClose, currentNode])

    // ─── Restart handler (returns to brief screen) ────────────
    const handleRestart = useCallback(() => {
        submitReport(CompletionState.Completed)
        onClose()
    }, [submitReport, onClose])

    // ─── Resolve background image for current node ────────────
    const isSceneNode = currentNode && SCENE_NODE_TYPES.has(currentNode.scenarioNodeType)
    const backgroundImageBlobUrl =
        !isSceneNode && currentNode?.backgroundImageUrl ? imageMap[currentNode.backgroundImageUrl] || null : null

    // ─── Error state ──────────────────────────────────────────
    if (!scenarioV1Dto || !stateMachine.startingNodeId) {
        return (
            <div className={classes.root}>
                <LessonHUD
                    progressPercent={0}
                    currentNodeIndex={0}
                    totalNodes={0}
                    currentNodeTypeName="Error"
                    lessonScore={0}
                    onQuit={onClose}
                />
                <div className={classes.contentArea}>
                    <Typography className={classes.errorText}>
                        <FormattedMessage {...playerMessages.errorNoStartingNode} />
                    </Typography>
                </div>
            </div>
        )
    }

    return (
        <div className={classes.root}>
            {/* HUD: progress bar + score + quit */}
            <LessonHUD
                progressPercent={progressPercent}
                currentNodeIndex={currentNodeIndex}
                totalNodes={linearNodeOrder.length}
                currentNodeTypeName={currentNodeTypeName}
                lessonScore={scoring.normalizedScore}
                onQuit={handleQuit}
            />

            {/* Main content area */}
            <div className={classes.contentArea}>
                {/* Background image */}
                {backgroundImageBlobUrl && (
                    <img src={backgroundImageBlobUrl} className={classes.backgroundImage} alt="" />
                )}

                <div
                    className={`${classes.contentOverlay} ${isSceneNode ? classes.contentOverlayScene : ''}`}>
                    <NodeRenderer
                        key={currentNode?.nodeId}
                        node={currentNode}
                        previousNode={previousNode}
                        imageMap={imageMap}
                        audioMap={audioMap}
                        onNodeComplete={handleNodeComplete}
                        onQuit={handleQuit}
                        scoring={scoring}
                        lessonDto={scenarioV1Dto}
                        onRestart={handleRestart}
                        isExamMode={isExamMode}
                        isPhone={isPhone}
                        l2LanguageId={scenarioV1Dto?.languageId}
                        l1Id={l1Id}
                        customLessonId={customLessonId}
                        isNativeSpeaker={isNativeSpeaker}
                    />
                </div>
            </div>
        </div>
    )
}

export default CapeeshPlayer
